import cls from "@fvilers/cls";
import { OutputIcon } from "@imi/icons/OutputIcon.js";
import { batchSelectionRepository } from "@src/core/engine/batchSelection/batchSelectionRepository";
import { processNumericValue } from "@src/core/engine/helpers/processNumericValue";
import { useUnitConversion } from "@src/shared/hooks/useUnitConversion";
import { useStore } from "@tanstack/react-form";
import { DeepKeys } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useBatchSelectionFiltersForm } from "../../../hooks/useBatchSelectionFiltersForm";
import { useCategoryId } from "../../../hooks/useCategoryId";
import { BatchSelectionRow } from "../../../store/types";
import { BatchSubProductCell } from "../../BatchSubProductCell";
import styles from "../BatchSelectionDataTable.module.css";
import { BatchOutputCell } from "../components/BatchOutputCell/BatchOutputCell";
import { getBatchOutputSortFn } from "../components/BatchOutputCell/helpers/getBatchOutputSortFn";
import { SuggestionName } from "../components/SuggestionName";
import { TableGroupHeader } from "../components/TableGroupHeader";
import { isFlowValid } from "../helpers/getColumnsValidation";
import { columnHelper } from "./useBatchSelectionDataTableColumns";
import { useOutputColumns } from "./useOutputColumns";

export function useBatchSelectionOutputColumns(
  form: ReturnType<typeof useBatchSelectionFiltersForm>["form"],
) {
  const { t } = useTranslation("product", {
    keyPrefix: "selection.batch.dataTable.columns",
  });
  const categoryId = useCategoryId();
  const { getUnitSymbol } = useUnitConversion();
  const useFlowOrPower = useStore(
    form.store,
    (state) => state.values.useFlowOrPower,
  );
  const outputColumns = useOutputColumns();
  const subProductsColumns = useMemo(
    () => batchSelectionRepository.getSubProductsTypes(categoryId),
    [categoryId],
  );

  return useMemo(
    () =>
      columnHelper.group({
        columns: columnHelper.columns([
          columnHelper.accessor("output.suggestion.name", {
            cell: (info) => <SuggestionName row={info.row.original} />,
            enableHiding: false,
            header: t("name"),
            id: "name",
            meta: {
              cell: {
                className: cls(styles.OutputCell, styles.SuggestionNameCell),
              },
              header: { className: styles.OutputHeader },
            },
          }),
          ...(useFlowOrPower === "flow"
            ? []
            : [
                columnHelper.accessor("flow", {
                  cell: (info) => {
                    const flow = info.getValue();
                    const flowValid =
                      isFlowValid(info.getValue()) && flow !== "";
                    if (!flowValid) return "";
                    const processed = processNumericValue(
                      Number(flow),
                      { maxDecimal: 3, physicalType: "PHYS_FLOW" },
                      { round: true },
                    );
                    return processed;
                  },
                  enableHiding: false,
                  header: t("flow", {
                    interpolation: { escapeValue: false },
                    unit: getUnitSymbol("PHYS_FLOW"),
                  }),
                  id: "flow-output",
                  meta: {
                    cell: { className: styles.OutputCell },
                    header: { className: styles.OutputHeader },
                  },
                  size: 120,
                }),
              ]),
          ...subProductsColumns.map((columnId) =>
            columnHelper.accessor(`output.suggestion.subProducts.${columnId}`, {
              cell: (info) => (
                <BatchSubProductCell row={info.row.original} type={columnId} />
              ),
              enableHiding: false,
              header: () => <span>{t(`subProducts.${columnId}`)}</span>,
              id: columnId,
              meta: { cell: { className: styles.SubProductCell } },
            }),
          ),
          ...outputColumns.map((columnId) => {
            const key = (
              columnId.startsWith("pipeResult")
                ? `output.${columnId}`
                : `output.suggestion.${columnId}`
            ) as DeepKeys<BatchSelectionRow>;

            return columnHelper.accessor(key, {
              cell: ({ row }) => (
                <BatchOutputCell column={columnId} row={row.original} />
              ),
              enableHiding: true,
              enableSorting: true,
              header: t(columnId),
              id: columnId,
              meta: {
                align: "right",
                cell: { className: styles.OutputCell },
                header: { className: styles.OutputHeader },
              },
              size: 120,
              sortFn: getBatchOutputSortFn,
            });
          }),
        ]),
        enableHiding: false,
        enableSorting: false,
        header: ({ column }) => (
          <TableGroupHeader
            hiddenColumns={column
              .getLeafColumns()
              .filter((c) => !c.getIsVisible())
              .toSorted((a, b) =>
                getGroupId(b.id).localeCompare(getGroupId(a.id)),
              )
              .map((column) => ({
                column,
                group: t(`hiddenColumnGroups.${getGroupId(column.id)}`),
              }))}
            icon={<OutputIcon />}
            label={t("groups.outputs")}
          />
        ),
        id: "outputs",
        meta: {
          draggable: false,
          header: {
            className: cls(
              styles.TableGroupHeader,
              styles.OutputsTableGroupHeader,
            ),
          },
        },
        size: 120,
      }),
    [getUnitSymbol, outputColumns, subProductsColumns, t, useFlowOrPower],
  );
}

function getGroupId(columnId: string) {
  const [group] = columnId.split(".");
  return group !== "articleNumber" ? group : "description";
}
